import { useEffect, useState } from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type Theme = 'light' | 'dark' | 'system';

/**
 * The themes on offer, and the order they are offered in.
 *
 * Here rather than in either component that renders them: the settings page and
 * the front door's account menu both write this store, and two copies drifted the
 * moment either was reordered or gained an option. It cannot live in a component
 * file either — `react-refresh/only-export-components` requires a module that
 * exports components to export nothing else.
 */
export const THEME_OPTIONS: ReadonlyArray<{ value: Theme; label: string }> = [
  { value: 'light', label: 'Light' },
  { value: 'system', label: 'System' },
  { value: 'dark', label: 'Dark' },
];
export type ResolvedTheme = 'light' | 'dark';

const STORAGE_KEY = 'product-x-next-theme';

function prefersDark(): boolean {
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

export function resolveTheme(theme: Theme): ResolvedTheme {
  if (theme === 'system') return prefersDark() ? 'dark' : 'light';
  return theme;
}

/**
 * Applies the resolved theme as a `dark` class on <html>, which is what the
 * `@custom-variant dark (&:is(.dark *))` rule in globals.css keys off.
 */
function applyTheme(theme: Theme): void {
  document.documentElement.classList.toggle(
    'dark',
    resolveTheme(theme) === 'dark',
  );
}

interface ThemeState {
  theme: Theme;
  setTheme: (theme: Theme) => void;
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set) => ({
      theme: 'system',
      setTheme: (theme) => {
        applyTheme(theme);
        set({ theme });
      },
    }),
    {
      name: STORAGE_KEY,
      onRehydrateStorage: () => (state) => {
        applyTheme(state?.theme ?? 'system');
      },
    },
  ),
);

/**
 * The theme as a component has to draw it: `light` or `dark`, never `system`.
 *
 * Most of the interface never needs this — it styles itself with tokens, and
 * the `dark` class on the document does the rest. Some things cannot: an
 * embedded editor, a canvas, anything that paints its own surface has to be
 * *told* which one it is on, and told again when that changes. Subscribes to
 * the media query as well as the store so a `system` reader following their OS
 * at dusk does not sit in front of a white page in a dark app.
 */
export function useResolvedTheme(): ResolvedTheme {
  const theme = useThemeStore((state) => state.theme);
  const [systemDark, setSystemDark] = useState(prefersDark);

  useEffect(() => {
    if (theme !== 'system') return;
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const sync = () => {
      setSystemDark(media.matches);
    };
    sync();
    media.addEventListener('change', sync);
    return () => {
      media.removeEventListener('change', sync);
    };
  }, [theme]);

  if (theme !== 'system') return theme;
  return systemDark ? 'dark' : 'light';
}

/**
 * Keeps a `system` preference in sync when the OS setting changes. Called once
 * from the app entry point; returns an unsubscribe function.
 */
export function watchSystemTheme(): () => void {
  const media = window.matchMedia('(prefers-color-scheme: dark)');
  const onChange = () => {
    if (useThemeStore.getState().theme === 'system') {
      applyTheme('system');
    }
  };
  media.addEventListener('change', onChange);
  return () => media.removeEventListener('change', onChange);
}
